import React, { useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Tooltip, Typography } from '@mui/material'
import { DataTable, SettingsPanel, TableShowMoreMenu } from '@vedmarket/ui-kit'
import moment from 'moment'

import { COLORS, APPLICATION_TABLE_COLUMN } from 'config/constants'
import { applicationDetailPath } from '../../../../utils/applicationRoutes'
import ApplicationStatusIcon from '../status-icon'
import { getApplicationStatusLabel } from '../status-label'

import './table.scss'

const REQUIRED_COLUMNS = [
  APPLICATION_TABLE_COLUMN.ID,
  APPLICATION_TABLE_COLUMN.REQUEST,
  APPLICATION_TABLE_COLUMN.STATUS,
  APPLICATION_TABLE_COLUMN.SETTINGS,
]

const PREFERENCES = [
  { id: APPLICATION_TABLE_COLUMN.ID, width: 104 },
  { id: APPLICATION_TABLE_COLUMN.REQUEST, width: 180 },
  { id: APPLICATION_TABLE_COLUMN.STATUS, width: 200 },
  { id: APPLICATION_TABLE_COLUMN.CALCULATION, width: 120 },
  { id: APPLICATION_TABLE_COLUMN.CLIENT, width: 128 },
  { id: APPLICATION_TABLE_COLUMN.CREATED_AT, width: 144 },
  { id: APPLICATION_TABLE_COLUMN.SETTINGS, width: 40 },
]

const ApplicationsTable = ({
  rows,
  count,
  limit,
  ordering = '-created_at',
  managerQueue,
  onRequest,
}: ApplicationTableProps) => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const allColumns = PREFERENCES.map((column) => column.id)
    .filter((id) => managerQueue || id !== APPLICATION_TABLE_COLUMN.CLIENT)
  const [ activeColumns, setActiveColumns ] = useState(allColumns)

  const labels: Record<ApplicationTableColumnId, string> = {
    [APPLICATION_TABLE_COLUMN.ID]: t('applications__text__columnId'),
    [APPLICATION_TABLE_COLUMN.REQUEST]: t('applications__text__columnRequest'),
    [APPLICATION_TABLE_COLUMN.STATUS]: t('applications__text__columnStatus'),
    [APPLICATION_TABLE_COLUMN.CALCULATION]: t('applications__text__calculation'),
    [APPLICATION_TABLE_COLUMN.CLIENT]: t('applications__text__columnClient'),
    [APPLICATION_TABLE_COLUMN.CREATED_AT]: t('applications__text__columnDate'),
    [APPLICATION_TABLE_COLUMN.SETTINGS]: '',
  }

  const handleChangeColumn = (id: APPLICATION_TABLE_COLUMN) => {
    setActiveColumns((current) => current.includes(id)
      ? current.filter((column) => column !== id)
      : [ ...current, id ])
  }

  const preferences = PREFERENCES.filter((column) => activeColumns.includes(column.id))
  const headCells = allColumns.filter((id) => activeColumns.includes(id)).map((id) => ({
    id,
    numeric: false,
    noSort: ![
      APPLICATION_TABLE_COLUMN.ID,
      APPLICATION_TABLE_COLUMN.STATUS,
      APPLICATION_TABLE_COLUMN.CREATED_AT,
    ].includes(id),
    label: id === APPLICATION_TABLE_COLUMN.SETTINGS ? (
      <SettingsPanel
        activeColumns={activeColumns}
        activeColumnsAll={allColumns}
        activeColumnsMin={REQUIRED_COLUMNS}
        excludedColumnIds={REQUIRED_COLUMNS}
        menuId='applications-settings-panel'
        selectAllLabel={t('calc__text__packaging__showHideEverything')}
        setActiveColumns={setActiveColumns}
        handleSelect={handleChangeColumn}
        getLabel={(column) => labels[column]}
      />
    ) : (
      <Typography
        variant='body2'
        sx={{
          fontWeight: 500,
          color: COLORS.BLACK_DARK,
          letterSpacing: 0,
        }}
      >
        {labels[id]}
      </Typography>
    ),
  }))

  const tableRows = rows.map((item) => {
    const detailUrl = applicationDetailPath({ publicId: item.public_id, managerView: managerQueue })
    const createdAt = moment(item.created_at)
    const createdDate = createdAt.format('DD.MM.YYYY')
    const createdTime = createdAt.format('HH:mm')
    const routeSummary = item.route_summary?.replace(/\s+[-–]\s+/g, ' — ') || '—'
    const adminRow = item as ApplicationAdminListItem
    const clientEmail = adminRow.user_email || '—'
    const calculationAmount = item.original_calculation_total
      ? `${item.original_calculation_total} ${item.original_calculation_currency || ''}`.trim()
      : ''
    const menuItems = [
      {
        title: <Typography variant='body1'>{t('actions__text__open')}</Typography>,
        onClick: () => navigate(detailUrl),
      },
    ]

    return {
      id: item.public_id,
      [APPLICATION_TABLE_COLUMN.ID]: (
        <Typography variant='body2'>{item.public_id}</Typography>
      ),
      [APPLICATION_TABLE_COLUMN.STATUS]: (
        <div className='applications-table__status'>
          <ApplicationStatusIcon status={item.status} label={item.status_label} />
          <Typography
            variant='body2'
            sx={{
              whiteSpace: 'nowrap',
            }}
          >
            {getApplicationStatusLabel({
              status: item.status,
              fallback: item.status_label,
              translate: t,
            })}
          </Typography>
        </div>
      ),
      [APPLICATION_TABLE_COLUMN.REQUEST]: (
        <RouterLink className='applications-table__request' to={detailUrl}>
          <Typography
            variant='body2'
          >
            {item.cargo_description || '—'}
          </Typography>
          <Typography
            variant='body2'
            sx={{
              color: COLORS.LIGHT_TEXT_SECONDARY,
            }}
          >
            {routeSummary}
          </Typography>
        </RouterLink>
      ),
      [APPLICATION_TABLE_COLUMN.CALCULATION]: (
        <div className='applications-table__calculation'>
          <Typography variant='body2'>{`№${item.calculation_number}`}</Typography>
          {calculationAmount && (
            <Typography
              variant='body2'
              sx={{
                whiteSpace: 'nowrap',
              }}
            >
              {calculationAmount}
            </Typography>
          )}
        </div>
      ),
      [APPLICATION_TABLE_COLUMN.CLIENT]: (
        <Tooltip title={<Typography variant='body2'>{clientEmail}</Typography>}>
          <Typography variant='body2' noWrap>{clientEmail}</Typography>
        </Tooltip>
      ),
      [APPLICATION_TABLE_COLUMN.CREATED_AT]: (
        <div className='applications-table__created-at'>
          <Typography variant='body2'>{createdDate}</Typography>
          <Typography variant='body2'>{createdTime}</Typography>
        </div>
      ),
      [APPLICATION_TABLE_COLUMN.SETTINGS]: <TableShowMoreMenu menuItems={menuItems} />,
    }
  })

  return (
    <div className='applications-table'>
      <DataTable
        rows={tableRows}
        count={count}
        headCells={headCells}
        preferences={preferences}
        primaryField='id'
        defaultOrderBy={ordering}
        defaultRowsPerPage={limit}
        rowsPerPageOptions={[ 10, 25, 50 ]}
        enableSelection={false}
        showPagination
        handleSendRequest={({ page = 1, limit: nextLimit = limit, ordering: nextOrdering = ordering }) => {
          onRequest({ page, limit: nextLimit, ordering: nextOrdering })
        }}
      />
    </div>
  )
}

export default ApplicationsTable
